import { createApi } from "@reduxjs/toolkit/query/react";
import { baseProxyQueryWithReauth } from "./API";
import { pageProps } from "../../helpers/pageProps";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = pageProps;

export type AssetType = {
  Uuid?: string;
  ClientId: number;
  Label: string;
  Description: string;
  Address: string;
  Reference: string;
  Kind: "asset";
  CreatedAt: string;
  UpdatedAt: string;
  HasLocation: boolean;
  HasManyLocations: boolean;
  CanMove: boolean;
  HasLocationHistory: boolean;
  Locations: any[];
  ParentAssets: AssetType[];
  ChildAssets: AssetType[];
  AssetContacts: any[];
  Stats?: {
    Locations: number;
    Contacts: number;
    Groups: number;
    Parents: number;
    Children: number;
    ActiveEvents: number;
  };
  Events?: any[];
};

export type LocationClusterType = {
  X: number;
  Y: number;
  IsCluster: boolean;
  ItemCount: number;
  Item: LocationType;
  ActiveEventAssetsCount?: number;
  ActiveEventsSeverity: {
    [key: string]: number;
  };
};

export type LocationType = {
  Uuid?: string;
  Description?: string;
  AssetUuid?: string;
  EventUuid?: string;
  ContactUuid?: string;
  IsCurrent: boolean;
  CreatedAt?: string;
  IsShape: boolean;
  IsDirect?: boolean;
  Contact?: any;
  Asset?: AssetType;
  Area?: {
    type: "MultiPolygon";
    coordinates: number[][][][]; // [ [ [ [ 1, 2 ], [ 3, 4 ] ] ] ]
  };
  Point?: {
    type: "Point";
    coordinates: number[]; // [ 1, 2 ]
  };
  Event?: any;
};

export type WeatherAlertRuleType = {
  ID: number;
  CreatedAt: string;
  ShapeId: number;
  GroupName: string;
  ClientId: number;
  Active: boolean;
  Priority: number;
  Description: string;
  Stop: boolean;
  Conditions: {
    ID: number;
    CreatedAt: string;
    AlertId: number;
    ClientId: number;
    Condition: string;
    ConditionOperator: string;
    ConditionValue: number;
    Description: string;
    Color: string;
    Order: number;
  }[];
  Shapes: null;
};

export type AssetsMapRequestType = {
  UseAssets?: boolean;
  UseContacts?: boolean;
  Coordinates?: number[][][]; // GeoJSON MultiPolygon coordinates
  IncludeAssets?: string[]; // optional list of asset UUIDs to include
  ExcludeAssets?: string[]; // optional list of asset UUIDs to exclude
  IncludeContacts?: string[]; // optional list of contact UUIDs to include
  ExcludeContacts?: string[]; // optional list of contact UUIDs to exclude
  IncludeGroups?: string[]; // optional list of group UUIDs to include
  ExcludeGroups?: string[]; // optional list of group UUIDs to exclude
  Page?: number;
  PerPage?: number;
};

// Define a service using a base URL and expected endpoints
export const mapsAPI = createApi({
  reducerPath: "mapsAPI",
  baseQuery: baseProxyQueryWithReauth("logic/workflows/api"),
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Assets",
    "Locations",
    "Contacts",
    "Groups",
    "ContactLocations",
    "AssetLocations",
    "WeatherAlerts",
    "WeatherAlertRules",
    "WeatherForecast",
  ],
  endpoints: (builder) => ({
    getAssets: builder.query<
      { assets: AssetType[]; total: number },
      {
        page?: number;
        perPage?: number;
        search?: string;
        kind?: string;
        assets?: string[];
        ignore?: string[];
      }
    >({
      query: (params) => ({
        url: `assets`,
        method: "GET",
        params,
      }),
      providesTags: ["Assets"],
    }),
    getAsset: builder.query<AssetType, string>({
      query: (uuid) => `assets/${uuid}`,
      transformResponse: (response: { asset: AssetType }, meta, arg) =>
        response.asset,
      providesTags: ["Assets"],
    }),
    upsertAsset: builder.mutation<any, Partial<AssetType>>({
      query: (body) => ({
        url: `assets`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Assets"],
    }),
    setAsParentAsset: builder.mutation<
      any,
      { children: string[]; assetUuid: string }
    >({
      query: ({ children, assetUuid }) => ({
        url: `assets/set-as-parent/${assetUuid}`,
        method: "POST",
        body: children,
      }),
      invalidatesTags: ["Assets"],
    }),
    addChildrenToParentAsset: builder.mutation<
      any,
      { children: string[]; assetUuid: string }
    >({
      query: ({ children, assetUuid }) => ({
        url: `assets/associations/add-parent/${assetUuid}`,
        method: "POST",
        body: children,
      }),
      invalidatesTags: ["Assets"],
    }),
    removeChildrenFromParentAsset: builder.mutation<
      any,
      { children: string[]; assetUuid: string }
    >({
      query: ({ children, assetUuid }) => ({
        url: `assets/associations/remove-parent/${assetUuid}`,
        method: "POST",
        body: children,
      }),
      invalidatesTags: ["Assets"],
    }),
    setAsChildAsset: builder.mutation<
      any,
      { parents: string[]; assetUuid: string }
    >({
      query: ({ parents, assetUuid }) => ({
        url: `assets/set-as-child/${assetUuid}`,
        method: "POST",
        body: parents,
      }),
      invalidatesTags: ["Assets"],
    }),
    addParentsToChildAsset: builder.mutation<
      any,
      { parents: string[]; assetUuid: string }
    >({
      query: ({ parents, assetUuid }) => ({
        url: `assets/associations/add-child/${assetUuid}`,
        method: "POST",
        body: parents,
      }),
      invalidatesTags: ["Assets"],
    }),
    removeParentsFromChildAsset: builder.mutation<
      any,
      { parents: string[]; assetUuid: string }
    >({
      query: ({ parents, assetUuid }) => ({
        url: `assets/associations/remove-child/${assetUuid}`,
        method: "POST",
        body: parents,
      }),
      invalidatesTags: ["Assets"],
    }),

    getChildrenAssets: builder.query<
      { assets: AssetType[]; total: number },
      string
    >({
      query: (uuid) => `assets/children/${uuid}`,
      providesTags: ["Assets"],
    }),
    getParentAssets: builder.query<
      { assets: AssetType[]; total: number },
      string
    >({
      query: (uuid) => `assets/parents/${uuid}`,
      providesTags: ["Assets"],
    }),
    getLocations: builder.query<
      { items: LocationClusterType[]; total: number },
      any
    >({
      query: (body) => ({
        url:
          body.Contacts && !body.Assets
            ? `locations/contacts/map`
            : `locations/map`,
        method: "POST",
        body,
      }),
      providesTags: ["Locations"],
    }),
    getLocationsWithoutClustering: builder.query<
      { items: LocationType[]; total: number },
      any
    >({
      query: (body) => ({
        url: "locations/map-data",
        method: "POST",
        body,
      }),
      providesTags: ["Locations"],
    }),
    getLocationsWithAssets: builder.query<
      { items: LocationType[]; total: number },
      any
    >({
      query: (body) => ({
        url: "locations/with-assets",
        method: "POST",
        body,
      }),
      providesTags: ["Locations"],
    }),
    getWeatherAlertsMap: builder.query<
      any,
      {
        west: number;
        north: number;
        east: number;
        south: number;
        centerLng: number;
        conditions?: string[];
        conditionIds?: number[];
        search?: string;
        start?: string;
        end?: string;
        radius?: number;
      }
    >({
      query: (params) => {
        return {
          url: `geo/map/alerts`,
          params,
        };
      },
      providesTags: ["WeatherAlerts"],
    }),
    getWeatherForecastMap: builder.query<
      any,
      {
        west: number;
        north: number;
        east: number;
        south: number;
        centerLng: number;
        start?: string;
        radius?: number;
        steps?: number; // number of 3 hour intervals
      }
    >({
      query: (params) => {
        return {
          url: `geo/map/weather-forecast`,
          params,
        };
      },
      providesTags: ["WeatherForecast"],
    }),
    getWeatherAlertsRules: builder.query<
      { alerts: WeatherAlertRuleType[]; total: number },
      any
    >({
      query: () => `geo/alerts/rules`,
      providesTags: ["WeatherAlertRules"],
    }),
    getAssetLocations: builder.query<
      { locations: LocationType[]; total: number },
      { uuid: string; page: number; perPage: number }
    >({
      query: ({ uuid, ...params }) => ({
        url: `locations/asset/${uuid}`,
        params,
      }),
      providesTags: ["Locations"],
    }),
    getAssetsByContact: builder.query<AssetType[], string>({
      query: (contactUuid) => `/assets/by-contact/${contactUuid}`,
      providesTags: ["Assets"],
      transformResponse: (response: { assets: AssetType[] }, meta, arg) =>
        response.assets,
    }),
    getContactsByAsset: builder.query<
      { contacts: any[]; total: number },
      { assetUuid: string; page: number; perPage: number }
    >({
      query: ({ assetUuid, page, perPage }) =>
        `/assets/contacts/${assetUuid}?page=${page}&perPage=${perPage}`,
      providesTags: ["Contacts"],
    }),
    getContactLocations: builder.query<LocationType[], string>({
      query: (contactUuid) => `locations/contact/${contactUuid}`,
      providesTags: ["Locations"],
      transformResponse: (response: { locations: LocationType[] }, meta, arg) =>
        response.locations,
    }),
    upsertAssetLocations: builder.mutation<
      any,
      { assetUuid: string; body: LocationType[] }
    >({
      query: ({ assetUuid, body }) => ({
        url: `locations/asset/${assetUuid}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations", "Assets"],
    }),
    deleteAssetLocations: builder.mutation<
      any,
      { assetUuid: string; body: string[] }
    >({
      query: ({ assetUuid, body }) => ({
        url: `locations-delete/asset/${assetUuid}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations", "Assets"],
    }),
    setContactsInLocation: builder.mutation<
      any,
      { Lat: number; Lng: number; items: string[] }
    >({
      query: (body) => ({
        url: `locations/contact`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations"],
    }),

    assignContactsToAsset: builder.mutation<
      any,
      { assetUuid: string; contactUuids: string[]; isDelete?: boolean }
    >({
      query: ({ assetUuid, contactUuids, isDelete }) => ({
        url: `assets/assign-contacts/${assetUuid}${isDelete ? "?delete" : ""}`,
        method: "POST",
        body: contactUuids,
      }),
      invalidatesTags: ["Assets", "Contacts"],
    }),

    removeContactsFromAsset: builder.mutation<
      any,
      { assetUuid: string; contactUuids: string[] }
    >({
      query: ({ assetUuid, contactUuids }) => ({
        url: `/assets/remove-contacts/${assetUuid}`,
        method: "POST",
        body: contactUuids,
      }),
      invalidatesTags: ["Assets", "Contacts"],
    }),

    removeLocationFromContacts: builder.mutation<
      any,
      { contactUuids: string[] }
    >({
      query: ({ contactUuids }) => ({
        url: `/locations/contact`,
        method: "POST",
        body: { Clear: true, Items: contactUuids },
      }),
      invalidatesTags: ["Contacts", "Locations"],
    }),

    removeAllContactsFromAsset: builder.mutation<any, { assetUuid: string }>({
      query: ({ assetUuid }) => ({
        url: `/assets/remove-contacts/${assetUuid}?delete=all `,
        method: "POST",
        body: [],
      }),
      invalidatesTags: ["Assets", "Contacts"],
    }),

    getGroupsByAsset: builder.query<
      { groups: any[]; total: number },
      { assetUuid: string; page: number; perPage: number }
    >({
      query: ({ assetUuid, page, perPage }) =>
        `/assets/groups/${assetUuid}?page=${page}&perPage=${perPage}`,
      providesTags: ["Groups"],
    }),

    assignGroupsToAsset: builder.mutation<
      any,
      { assetUuid: string; groupUuids: string[]; isDelete?: boolean }
    >({
      query: ({ assetUuid, groupUuids, isDelete }) => ({
        url: `assets/assign-groups/${assetUuid}${isDelete ? "?delete" : ""}`,
        method: "POST",
        body: groupUuids,
      }),
      invalidatesTags: ["Assets", "Groups"],
    }),

    removeGroupsFromAsset: builder.mutation<
      any,
      { assetUuid: string; groupUuids: string[] }
    >({
      query: ({ assetUuid, groupUuids }) => ({
        url: `/assets/remove-groups/${assetUuid}`,
        method: "POST",
        body: groupUuids,
      }),
      invalidatesTags: ["Assets", "Groups"],
    }),

    listContactsAndGroupsInLocation: builder.query<any, AssetsMapRequestType>({
      query: (body) => ({
        url: `assets-map/contacts-and-groups`,
        method: "POST",
        body,
      }),
      providesTags: ["ContactLocations"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAssetsQuery,
  useGetAssetQuery,
  useGetAssetsByContactQuery,
  useGetContactsByAssetQuery,
  useUpsertAssetMutation,
  useGetChildrenAssetsQuery,
  useGetParentAssetsQuery,
  useSetAsChildAssetMutation,
  useSetAsParentAssetMutation,
  useGetLocationsQuery,
  useGetWeatherAlertsMapQuery,
  useGetWeatherAlertsRulesQuery,
  useUpsertAssetLocationsMutation,
  useDeleteAssetLocationsMutation,
  useGetAssetLocationsQuery,
  useGetContactLocationsQuery,
  useSetContactsInLocationMutation,
  useAssignContactsToAssetMutation,
  useRemoveContactsFromAssetMutation,
  useRemoveAllContactsFromAssetMutation,
  useRemoveLocationFromContactsMutation,
  useGetWeatherForecastMapQuery,
  useAddChildrenToParentAssetMutation,
  useRemoveChildrenFromParentAssetMutation,
  useAddParentsToChildAssetMutation,
  useRemoveParentsFromChildAssetMutation,
  useGetLocationsWithoutClusteringQuery,
  useGetGroupsByAssetQuery,
  useAssignGroupsToAssetMutation,
  useRemoveGroupsFromAssetMutation,
  useGetLocationsWithAssetsQuery,
  useListContactsAndGroupsInLocationQuery,
} = mapsAPI;
